/*
Licensed Materials - Property of IBM
694906H
(c) Copyright IBM Corp.  2020 All Rights Reserved

US Government Users Restricted Rights - Use, duplication or disclosure restricted
by GSA ADP Schedule Contract with IBM Corp.
*/

import { gql } from '@apollo/client';
import {
  getClientImagePath,
  renderDefaultError,
  renderDefaultLoading,
  SmartComponentProps
} from '@exo/frontend-common-utils';
import { useProduct } from '../../hooks/useProduct';
import { ProductRef } from '../../model/product-ref';
import { useNavigate } from 'react-router-dom';
import { generateUrlWithLanguage } from '../../../../../../utils';
import { useSessionContext } from '@exo/frontend-common-session-context';

export const ProductImagesContainer = ({
  productId,
  render,
  renderError = renderDefaultError,
  renderLoading = renderDefaultLoading
}: Props) => {
  const navigate = useNavigate();
  const { loading, data, error } = useProduct<ProductImagesResponse>(
    { productId },
    ProductImagesContainer.fragment
  );
  const session = useSessionContext()

  if (loading) return renderLoading();
  if (error || data?.product === null){
    navigate(generateUrlWithLanguage('/error',session.language))
    return;
  };


  const additionalImages =
    data?.product?.attachments
      ?.filter(a => a.type === 'image' && (a.subType === 'THUMBNAIL' || a.subType === 'VIDEO'))
      .map(a => getClientImagePath(a.url)) ?? [];
  
  return render({
    fullImage: getClientImagePath(data?.product?.fullImage),
    additionalImages:
      additionalImages.length > 0
        ? additionalImages
        : [getClientImagePath(data?.product?.fullImage)]
  });
};

ProductImagesContainer.fragment = gql`
  fragment ProductImagesContainer on PrdItem {
    id
    fullImage
    attachments {
      type
      url
      subType
      mimeType
    }
  }
`;

type ProductImagesResponse = {
  id: string;
  fullImage?: string;
  attachments?: {
    type: string;
    url: string;
    subType: string;
    mimeType: string;
  }[];
};

type Props = SmartComponentProps<{
  productId: ProductRef;
  render: (props: ProductImagesContainerRenderProps) => JSX.Element;
}>;

export type ProductImagesContainerRenderProps = {
  fullImage: string;
  additionalImages: string[];
};
